import React, { useEffect, useState } from "react";
import { gameActor } from "../hooks/useGameMachine.js";
import { PartyStore } from "../../store/PartyStore.js";
import {
  listAdventures,
  type AdventureListingEntry,
} from "../../engine/adventure/adventureListing.js";
import {
  listSlots,
  peek,
  type SaveSlotSummary,
} from "../../engine/persistence/PersistenceManager.js";
import { bootAdventure } from "../../engine/adventure/bootAdventure.js";
import { markPendingIntro } from "../../engine/narrative/introGate.js";
import { mintPartyId } from "../../engine/party/partyIdentity.js";
import { cloneRosterToL1 } from "../../engine/party/reuseParty.js";
import {
  makeBlueberriesParty,
  BLUEBERRIES_PARTY_NAME,
  equipBlueberries,
} from "../../engine/party/blueberries.js";
import {
  loadPregenRoster,
  makePregenParty,
  equipPregens,
  type PregenRoster,
} from "../../engine/party/pregens.js";
import type { Character } from "../../engine/types/entity.js";

/**
 * Adventure picker for the launch gate (P21 + P22).
 *
 * Two-stage flow:
 *   1. Pick an adventure (list pulled from `/engine/adventures.json`).
 *   2. Pick "Fresh party" or one of the existing parties to reuse.
 *
 * Fresh-party path: boot the adventure, send BEGIN_NEW_GAME to enter
 * `characterCreation`, and prime the loader with the adventure's entry map
 * (LOAD_MAP as an action — the game stays in `characterCreation` until
 * PARTY_READY transitions to `loading`).
 *
 * Reuse-party path: read the source party's most recent save via `peek`
 * (which does NOT hydrate stores — we don't want to overwrite
 * WorldStore/SessionStore with old values), clone the roster to L1, mint a
 * fresh `partyId`, stamp PartyStore with the new identity + members, then
 * follow the same BEGIN_NEW_GAME path. NewGameFlow detects the pre-populated
 * PartyStore and skips straight to the BetweenPanel so the player can
 * rename the party before starting.
 */
export function LaunchAdventurePicker() {
  const [entries, setEntries] = useState<readonly AdventureListingEntry[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdventureListingEntry | null>(null);
  const [saves, setSaves] = useState<readonly SaveSlotSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);
  // EV-E8: adventure-authored pregen roster; replaces the Blueberries
  // tile when the selected adventure ships data/party/pregens.json.
  const [pregens, setPregens] = useState<PregenRoster | null>(null);
  useEffect(() => {
    setPregens(null);
    if (!selected) return;
    const dataRoot = selected.manifest.assetRoots?.data ?? "data";
    void loadPregenRoster(`/${selected.id}/${dataRoot}/party/pregens.json`).then(setPregens);
  }, [selected]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listAdventures(), listSlots()])
      .then(([adv, slots]) => {
        if (cancelled) return;
        setEntries(adv);
        setSaves(slots);
      })
      .catch((e: unknown) => {
        if (!cancelled) setListError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const partiesForReuse = saves ? derivePartiesForReuse(saves) : [];

  const startFreshParty = async (entry: AdventureListingEntry) => {
    setBusy(true);
    setOpError(null);
    try {
      await bootAdventure(entry.id);
      // Make sure PartyStore is clean (reuse-fork may have populated it
      // earlier in this session if the user backed out).
      PartyStore._reset();
      markPendingIntro();
      gameActor.send({ type: "BEGIN_NEW_GAME" });
      gameActor.send({ type: "LOAD_MAP", mapId: entry.manifest.entryMap });
    } catch (e: unknown) {
      setOpError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const startReuseParty = async (entry: AdventureListingEntry, sourceSlotId: string, sourcePartyName: string) => {
    setBusy(true);
    setOpError(null);
    try {
      const sourceFile = await peek(sourceSlotId);
      const sourceMembers = sourceFile.state.party.members as Character[];
      const clones = cloneRosterToL1(sourceMembers);
      const newId = mintPartyId();
      // Mount adventure AFTER peeking so the peek doesn't depend on the
      // active-adventure binding; `peek` uses migrateSaveFile only.
      await bootAdventure(entry.id);
      PartyStore._reset();
      PartyStore.setMembers(clones);
      PartyStore.setPartyIdentity(newId, sourcePartyName);
      markPendingIntro();
      gameActor.send({ type: "BEGIN_NEW_GAME" });
      gameActor.send({ type: "LOAD_MAP", mapId: entry.manifest.entryMap });
    } catch (e: unknown) {
      setOpError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const startPregenParty = async (entry: AdventureListingEntry, roster: PregenRoster) => {
    setBusy(true);
    setOpError(null);
    try {
      await bootAdventure(entry.id);
      PartyStore._reset();
      PartyStore.setMembers(makePregenParty(roster));
      PartyStore.setPartyIdentity(mintPartyId(), roster.title);
      await equipPregens(roster, PartyStore.getMembers());
      markPendingIntro();
      gameActor.send({ type: "BEGIN_NEW_GAME" });
      gameActor.send({ type: "LOAD_MAP", mapId: entry.manifest.entryMap });
    } catch (e: unknown) {
      setOpError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  const startBlueberriesParty = async (entry: AdventureListingEntry) => {
    setBusy(true);
    setOpError(null);
    try {
      await bootAdventure(entry.id);
      PartyStore._reset();
      PartyStore.setMembers(makeBlueberriesParty());
      PartyStore.setPartyIdentity(mintPartyId(), BLUEBERRIES_PARTY_NAME);
      await equipBlueberries(PartyStore.getMembers());
      markPendingIntro();
      gameActor.send({ type: "BEGIN_NEW_GAME" });
      gameActor.send({ type: "LOAD_MAP", mapId: entry.manifest.entryMap });
    } catch (e: unknown) {
      setOpError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div data-testid="launch-adventure-picker" style={rootStyle}>
      <div style={headerRow}>
        <h1 style={{ margin: 0, fontSize: 20, letterSpacing: 3 }}>
          {selected ? `NEW — ${selected.manifest.title.toUpperCase()}` : "NEW GAME"}
        </h1>
        <button
          type="button"
          onClick={() => {
            if (selected) {
              setSelected(null);
            } else {
              gameActor.send({ type: "SHOW_LAUNCH" });
            }
          }}
          style={backBtnStyle}
        >
          ← Back
        </button>
      </div>

      {listError && <div style={errStyle}>Failed to list adventures: {listError}</div>}
      {opError && <div style={errStyle}>{opError}</div>}
      {entries === null && !listError && <div style={mutedStyle}>Loading…</div>}

      {!selected &&
        entries &&
        entries.length === 0 &&
        !listError && (
          <div style={mutedStyle}>
            No adventures installed. Add one under <code>public/</code>.
          </div>
        )}

      {/* Stage 1: pick adventure */}
      {!selected && entries && entries.length > 0 && (
        <div style={gridStyle}>
          {entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              data-testid={`adventure-card-${entry.id}`}
              onClick={() => {
                setSelected(entry);
              }}
              style={cardBtnStyle}
            >
              <div style={{ fontSize: 15, marginBottom: 6 }}>{entry.manifest.title}</div>
              <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>
                by {entry.manifest.author} · v{entry.manifest.version}
              </div>
              <div style={{ fontSize: 10, color: "#666" }}>
                entry {entry.manifest.entryMap}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Stage 2: fresh vs reuse */}
      {selected && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button
            type="button"
            data-testid="fresh-party-btn"
            disabled={busy}
            onClick={() => void startFreshParty(selected)}
            style={{ ...cardBtnStyle, borderColor: "#2a6" }}
          >
            <div style={{ fontSize: 15, marginBottom: 4 }}>Fresh party</div>
            <div style={{ fontSize: 11, color: "#aaa" }}>
              Roll new characters with the character creator.
            </div>
          </button>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 8, letterSpacing: 2 }}>
              OR REUSE AN EXISTING PARTY
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pregens ? (
                <button
                  type="button"
                  data-testid="pregen-party-btn"
                  disabled={busy}
                  onClick={() => void startPregenParty(selected, pregens)}
                  style={{ ...cardBtnStyle, borderColor: "#4a7bc4" }}
                >
                  <div style={{ fontSize: 13, color: "#9cc1ff" }}>{pregens.title}</div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    {pregens.blurb ||
                      pregens.members
                        .map((m) => `${m.name} (${m.characterClass})`)
                        .join(", ")}
                  </div>
                  <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                    Preset L1 party — skip chargen.
                  </div>
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="blueberries-party-btn"
                  disabled={busy}
                  onClick={() => void startBlueberriesParty(selected)}
                  style={{ ...cardBtnStyle, borderColor: "#4a7bc4" }}
                >
                  <div style={{ fontSize: 13, color: "#9cc1ff" }}>
                    {BLUEBERRIES_PARTY_NAME}
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    Bram Tusk (Fighter), Hux Quill (Rogue), Sister Ilka (Cleric), Cobalt Vex (Wizard)
                  </div>
                  <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                    Preset L1 party — skip chargen.
                  </div>
                </button>
              )}
              {partiesForReuse.map((p) => (
                <button
                  key={`${p.adventureId}::${p.partyId}`}
                  type="button"
                  data-testid={`reuse-party-${p.partyId}`}
                  disabled={busy}
                  onClick={() =>
                    void startReuseParty(selected, p.sourceSlotId, p.partyName)
                  }
                  style={cardBtnStyle}
                >
                  <div style={{ fontSize: 13 }}>{p.partyName}</div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    from {p.adventureId} · {p.roster}
                  </div>
                  <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                    Roster will be cloned at level 1.
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Derive-parties helper ───────────────────────────────────────────────────
// Exported for P23 tests. One entry per `(adventureId, partyId)`, pinned to
// the most-recent slot in that group so the reuse path can peek without a
// second listing pass.

interface ReusablePartyListEntry {
  readonly adventureId: string;
  readonly partyId: string;
  readonly partyName: string;
  readonly roster: string;
  readonly sourceSlotId: string;
  readonly sourceTimestamp: string;
}

export function derivePartiesForReuse(
  slots: readonly SaveSlotSummary[],
): readonly ReusablePartyListEntry[] {
  const byKey = new Map<string, ReusablePartyListEntry>();
  for (const s of slots) {
    const key = `${s.adventureId}::${s.meta.partyId}`;
    const existing = byKey.get(key);
    if (!existing || s.meta.timestamp > existing.sourceTimestamp) {
      byKey.set(key, {
        adventureId: s.adventureId,
        partyId: s.meta.partyId,
        partyName: s.meta.partyName,
        roster: s.meta.party.map((p) => `${p.name} L${String(p.level)}`).join(", "),
        sourceSlotId: s.slotId,
        sourceTimestamp: s.meta.timestamp,
      });
    }
  }
  return [...byKey.values()].sort((a, b) =>
    b.sourceTimestamp.localeCompare(a.sourceTimestamp),
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const rootStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "#0a0a0a",
  color: "white",
  fontFamily: "monospace",
  padding: 32,
  overflowY: "auto",
  zIndex: 200,
  pointerEvents: "auto",
};

const headerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 24,
};

const backBtnStyle: React.CSSProperties = {
  background: "#1a1a1a",
  color: "white",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 4,
  padding: "6px 12px",
  fontFamily: "monospace",
  fontSize: 12,
  cursor: "pointer",
};

const gridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: 16,
};

const cardBtnStyle: React.CSSProperties = {
  textAlign: "left",
  background: "#1a1a1a",
  border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 6,
  padding: 16,
  color: "white",
  cursor: "pointer",
  fontFamily: "monospace",
};

const errStyle: React.CSSProperties = {
  color: "#c66",
  fontSize: 13,
  marginBottom: 16,
};

const mutedStyle: React.CSSProperties = {
  color: "#888",
  fontSize: 13,
};
